import * as THREE from 'three';
import {makeZeroIfUndefined} from 'components/customer/Preview3D/helpers';
import {TemplateVariable} from 'components/customer/Preview3D/usePreview3DData';
import {
    Batch,
    Drawer,
    PreviewFormValues,
    OperationGroup,
    LShapeVariant,
    LShapeDoorTuning,
    NormalizeLShapeInputs,
    detectLShapeVariant,
    resolveDoorThickness,
} from 'components/customer/Preview3D/types';
import {RoomPlannerFormValues} from 'components/customer/RoomPlanner/types';

type FormValues = {
    width: number;
    doorGap: number;
    height: number;
    doorTop: number;
    doorBottom: number;
    doorLeft: number;
    doorRight: number;
    drawerTop: number;
    returnPanelWidth: number;
    cabinetVoidWidth: number;
    leftWidth: number;
    rightDepth: number;
    rightWidth: number;
    leftDepth: number;
    drawers: Drawer[];
    drawerAmount: number;
    microwaveOpeningHeight: number;
};

export const getDoorFormValues = (values: PreviewFormValues) => {
    return {
        width: makeZeroIfUndefined(values.cabinet_width),
        height: makeZeroIfUndefined(values.cabinet_height),
        doorGap: makeZeroIfUndefined(values.cabinet_door_gap),
        doorTop: makeZeroIfUndefined(values.cabinet_door_top),
        doorBottom: makeZeroIfUndefined(values.cabinet_door_bottom),
        doorLeft: makeZeroIfUndefined(values.cabinet_door_left),
        doorRight: makeZeroIfUndefined(values.cabinet_door_right),
        drawerTop: makeZeroIfUndefined(values.cabinet_drawer_top),
        returnPanelWidth: makeZeroIfUndefined(
            values.cabinet_return_panel_width
        ),
        cabinetVoidWidth:
            values.cabinet_void_width && Boolean(values.cabinet_cover_void)
                ? makeZeroIfUndefined(values.cabinet_void_width)
                : 0,
        drawers: values.drawers,
        drawerAmount: values.drawer_amount,
        leftWidth: makeZeroIfUndefined(values.cabinet_left_width),
        rightDepth: makeZeroIfUndefined(values.cabinet_right_depth),
        rightWidth: makeZeroIfUndefined(values.cabinet_right_width),
        leftDepth: makeZeroIfUndefined(values.cabinet_left_depth),
        microwaveOpeningHeight: makeZeroIfUndefined(
            values.microwave_opening_height
        ),
    };
};

export const generateDoorPosition = (
    variables: TemplateVariable,
    values: RoomPlannerFormValues,
    formValues?: FormValues
) => {
    const doorBottomMargin = formValues.doorBottom - 1;
    const doorLeftMargin = formValues.doorLeft;
    const doorRightMargin = formValues.doorRight;
    const doorTopMargin = formValues.doorTop;

    const carcaseThickness = values.cabinet_carc_thickness;
    const exteriorThickness = values.cabinet_ext_thickness;
    const carcaseThicknessValue = variables.isCabinetUsingExterior
        ? exteriorThickness
        : carcaseThickness;

    const doorWidth = formValues.doorGap
        ? (formValues.width + carcaseThicknessValue) / 2 -
          formValues.doorGap / 2
        : (formValues.width + carcaseThicknessValue) / 2;
    const singleDoorWidth =
        formValues.width +
        carcaseThicknessValue -
        doorRightMargin -
        doorLeftMargin;

    // Corner "hang" door spans the diagonal chamfer face, so BOTH side reveals
    // inset it (left edge by doorLeft, right edge by doorRight). The right
    // reveal was dropped when corner doors were funnelled onto this formula
    // during the CO-4895/CO-4950 render consolidation, so the right margin
    // stopped doing anything.
    const cornerHangDoorWidth =
        (formValues.leftWidth - formValues.rightDepth) * 1.42 +
        2 -
        doorLeftMargin -
        doorRightMargin;

    const thicknessDiff = exteriorThickness - carcaseThickness;
    const singleDoorLocation = singleDoorWidth + doorLeftMargin + 1;
    const rightBifoldDoorWidth = formValues.rightWidth - formValues.leftDepth;
    const leftBifoldDoorWidth = formValues.leftWidth - formValues.rightDepth;

    const thicknessDiffHalf = thicknessDiff / 2;

    let wallOvenHeight = 0;
    if (variables?.isWallOvenProduct) {
        wallOvenHeight =
            Number(values.cabinet_total_drawer_height) +
            Number(values.oven_opening_height) +
            formValues.microwaveOpeningHeight;
    } else if (variables?.hasUpperPairCabinet) {
        wallOvenHeight = formValues.microwaveOpeningHeight + exteriorThickness;
    }

    return {
        leftDoorWidth: doorWidth - doorLeftMargin + 1,
        rightDoorWidth: doorWidth - doorRightMargin - 1,
        rightDoorLocation: doorWidth + formValues.doorGap + 1,
        leftDoorLocation: doorWidth - 1,
        doorHeight:
            formValues.height -
            doorTopMargin -
            doorBottomMargin -
            wallOvenHeight,
        doorBottomMargin: doorBottomMargin + wallOvenHeight,
        doorLeftMargin,
        doorTopMargin,
        singleDoorWidth: singleDoorWidth + 1,
        singleDoorLocation: singleDoorLocation - 1,
        singleDoorLocationRightHang: singleDoorLocation + 1,
        returnPanelWidth: formValues.returnPanelWidth,
        leftBifoldDoorWidth:
            leftBifoldDoorWidth - doorLeftMargin - thicknessDiff,
        rightBifoldDoorWidth:
            rightBifoldDoorWidth - doorRightMargin - thicknessDiff,
        rightBifoldDoorX: formValues.leftDepth + exteriorThickness,
        rightBifoldDoorY: formValues.leftWidth - formValues.rightDepth,
        leftBifoldDoorY: -carcaseThickness + doorLeftMargin,
        cornerHangDoorWidth,
        // Pair splits the (both-reveal-inset) hang width with the door gap as
        // the seam between them: 2*pairWidth + doorGap == cornerHangDoorWidth.
        cornerPairDoorWidth: (cornerHangDoorWidth - formValues.doorGap) / 2,
        leftCornerDoorX: formValues.leftDepth,
        rightCornerDoorX: formValues.leftDepth + 2,
        rightCornerDoorY: -exteriorThickness,
        doorGap: formValues.doorGap,
        wallOvenHeight,
        ...(variables?.is3DoorProduct
            ? {
                  threeDoor: {
                      rightBifoldDoorWidth:
                          rightBifoldDoorWidth / 2 - thicknessDiffHalf,
                      rightBifold2ndDoorX:
                          formValues.leftDepth +
                          thicknessDiffHalf +
                          carcaseThickness +
                          rightBifoldDoorWidth / 2 +
                          1,
                      rightBifold2ndDoorWidth:
                          rightBifoldDoorWidth / 2 -
                          thicknessDiffHalf -
                          doorRightMargin +
                          1,
                      leftBifoldDoorWidth:
                          leftBifoldDoorWidth / 2 - thicknessDiffHalf + 1,
                      leftBifoldDoorY:
                          -carcaseThickness + leftBifoldDoorWidth / 2,
                      leftBifold2ndDoorY:
                          leftBifoldDoorWidth / 2 -
                          thicknessDiffHalf -
                          carcaseThickness,
                  },
              }
            : {}),
    };
};

export const generateReturnDoor = (
    variables: TemplateVariable,
    values: RoomPlannerFormValues,
    formValues?: FormValues
) => {
    const doorLeftMargin = formValues.doorLeft;
    const doorRightMargin = formValues.doorRight;
    const carcaseThickness = values.cabinet_carc_thickness;

    const doorDefault = generateDoorPosition(variables, values, formValues);
    const width = formValues.width - doorDefault.returnPanelWidth;

    const doorWidth = formValues.doorGap
        ? (width + carcaseThickness) / 2 - formValues.doorGap / 2
        : (width + carcaseThickness) / 2;

    const voidWidthHalf = formValues.cabinetVoidWidth
        ? formValues.cabinetVoidWidth / 2
        : 0;
    const leftDoorWidth = doorWidth - doorLeftMargin + voidWidthHalf;
    const rightDoorWidth = doorWidth - doorRightMargin + voidWidthHalf;
    const singleDoorWidth =
        formValues.width -
        doorDefault.returnPanelWidth +
        carcaseThickness -
        doorRightMargin -
        doorLeftMargin +
        formValues.cabinetVoidWidth;
    const singleDoorLocation =
        singleDoorWidth + doorLeftMargin + doorDefault.returnPanelWidth;

    return {
        ...doorDefault,
        leftDoorWidth,
        rightDoorWidth,
        rightDoorLocation:
            doorWidth +
            doorDefault.returnPanelWidth +
            formValues.doorGap -
            voidWidthHalf +
            2,
        leftDoorLocation:
            doorWidth + doorDefault.returnPanelWidth - voidWidthHalf - 2,
        singleDoorWidth,
        singleDoorLocation: singleDoorLocation - formValues.cabinetVoidWidth,
        returnPanelLocation:
            doorDefault.returnPanelWidth - formValues.cabinetVoidWidth,
        rightReturn: {
            leftDoorWidth,
            rightDoorWidth,
            rightDoorLocation:
                doorWidth +
                formValues.doorGap +
                formValues.cabinetVoidWidth -
                voidWidthHalf,
            leftDoorLocation: doorWidth + voidWidthHalf,
            returnPanelLocation:
                doorDefault.returnPanelWidth +
                leftDoorWidth +
                rightDoorWidth +
                doorLeftMargin +
                doorRightMargin +
                formValues.doorGap,
            singleDoorWidth,
            singleDoorLocation:
                singleDoorLocation - doorDefault.returnPanelWidth,
        },
    };
};

export const generateApplianceDoor = (
    variables: TemplateVariable,
    values: RoomPlannerFormValues,
    formValues?: FormValues
) => {
    const doorDefault = generateDoorPosition(variables, values, formValues);
    const carcaseThickness = values.cabinet_carc_thickness;
    const width = formValues.width + carcaseThickness;
    const doorRightMargin = formValues.doorRight;
    const doorLeftMargin = formValues.doorLeft;
    const doorTopMargin = formValues.doorTop;
    const doorGap = formValues.doorGap;

    const fasciaWidth = 28;
    const threeDoorWidth = (width - doorGap * 2) / 3;
    const fourDoorWidth = (width - doorGap * 3) / 4;

    let leftBifoldDoorWidth = threeDoorWidth - doorLeftMargin;
    let leftBifoldDoorLocation = threeDoorWidth;
    let rightBifoldDoorWidth = threeDoorWidth + 1;
    let rightBifoldDoorLocation = threeDoorWidth + doorGap + 2;

    if (variables?.isApplianceLeftRightBifold) {
        leftBifoldDoorWidth = threeDoorWidth + 1;
        leftBifoldDoorLocation = threeDoorWidth * 2 + doorGap;
        rightBifoldDoorWidth = threeDoorWidth - doorRightMargin + 1;
        rightBifoldDoorLocation = width - threeDoorWidth + 2;
    }

    const isApplianceComplexBifold =
        variables?.isApplianceLeftBifoldRight ||
        variables?.isApplianceLeftRightBifold;

    return {
        ...doorDefault,
        rightDoorWidth: variables?.isApplianceLeftBifoldRight
            ? threeDoorWidth - doorRightMargin
            : doorDefault.rightDoorWidth,
        rightDoorLocation: width - threeDoorWidth + 2,
        leftDoorWidth: variables?.isApplianceLeftRightBifold
            ? threeDoorWidth - doorLeftMargin + 2
            : doorDefault.leftDoorWidth,
        leftDoorLocation: threeDoorWidth,
        bifold: {
            leftDoorWidth: isApplianceComplexBifold
                ? leftBifoldDoorWidth
                : doorDefault.leftDoorWidth,
            leftDoorLocation: isApplianceComplexBifold
                ? leftBifoldDoorLocation
                : doorDefault.leftDoorLocation,
            rightDoorWidth: isApplianceComplexBifold
                ? rightBifoldDoorWidth
                : doorDefault.rightDoorWidth,
            rightDoorLocation: isApplianceComplexBifold
                ? rightBifoldDoorLocation
                : doorDefault.rightDoorLocation,
        },
        ...(variables?.isApplianceBifoldPair
            ? {
                  bifoldPair: {
                      leftPairLeftWidth: fourDoorWidth - doorLeftMargin + 1,
                      leftPairLeftLocation: fourDoorWidth - 1,

                      leftPairRightWidth: fourDoorWidth,
                      leftPairRightLocation: fourDoorWidth + doorGap + 1,

                      rightPairLeftWidth: fourDoorWidth + 1,
                      rightPairLeftLocation:
                          fourDoorWidth * 3 + doorGap * 2 - 2,

                      rightPairRightWidth: fourDoorWidth - doorRightMargin + 2,
                      rightPairRightLocation: fourDoorWidth * 3 + doorGap * 3,
                  },
              }
            : {}),
        fascia: {
            topWidth: formValues.width - carcaseThickness - fasciaWidth * 2 + 3,
            topHeight:
                carcaseThickness +
                fasciaWidth -
                (doorTopMargin < 0 ? doorTopMargin : 0),
            topPositionX: carcaseThickness + fasciaWidth - 2,
            topPositionZ:
                formValues.height -
                carcaseThickness -
                fasciaWidth -
                (doorTopMargin < 0 ? 0 : doorTopMargin),
            leftWidth: carcaseThickness + fasciaWidth - doorLeftMargin,
            rightWidth: carcaseThickness + fasciaWidth - doorRightMargin,
            leftPositionX: carcaseThickness + fasciaWidth,
            rightPositionX: formValues.width - fasciaWidth,
            sidePozitionZ: -1,
            doorHeight: formValues.height - formValues.doorTop + 1,
        },
    };
};

export const generateDoorDrawer = (formValues?: FormValues) => {
    if (!formValues.drawers || !formValues.drawers[0]) return {};

    const faceHeight = formValues.drawers[0].drawer_face_height;
    const doorBottomMargin = formValues.doorBottom;

    return {
        doorHeight:
            formValues.height -
            formValues.doorTop -
            doorBottomMargin -
            (faceHeight + formValues.drawerTop),
    };
};

export const defaultLShapeDoorTuning: LShapeDoorTuning = {
    perimeterAnchor: 1,
    depthGap: 2,
    cornerGap: 2,
    openCompensationFactor: 2,
    leftSmallInsertYAdjust: -1.5,
};

const setPerimeterRect = (
    og: OperationGroup,
    xMin: number,
    xMax: number,
    yMin: number,
    yMax: number
) => {
    for (const op of og.Operations ?? []) {
        if (op.Type !== 'perimeter_shape' || !op.Segments) continue;
        let oldXMin = Infinity;
        let oldXMax = -Infinity;
        let oldYMin = Infinity;
        let oldYMax = -Infinity;
        for (const seg of op.Segments) {
            for (const c of [seg.StartCoor, seg.EndCoor]) {
                if (c?.x !== undefined) {
                    if (c.x < oldXMin) oldXMin = c.x;
                    if (c.x > oldXMax) oldXMax = c.x;
                }
                if (c?.y !== undefined) {
                    if (c.y < oldYMin) oldYMin = c.y;
                    if (c.y > oldYMax) oldYMax = c.y;
                }
            }
        }
        for (const seg of op.Segments) {
            for (const c of [seg.StartCoor, seg.EndCoor]) {
                if (!c) continue;
                if (c.x !== undefined) {
                    if (Math.abs(c.x - oldXMin) < 0.01) c.x = xMin;
                    else if (Math.abs(c.x - oldXMax) < 0.01) c.x = xMax;
                }
                if (c.y !== undefined) {
                    if (Math.abs(c.y - oldYMin) < 0.01) c.y = yMin;
                    else if (Math.abs(c.y - oldYMax) < 0.01) c.y = yMax;
                }
            }
        }
    }
};

const setPanelInsert = (
    og: OperationGroup,
    x: number,
    y: number,
    z?: number
) => {
    for (const op of og.Operations ?? []) {
        if (op.Type !== 'panel' || !op.Insert) continue;
        op.Insert.x = x;
        op.Insert.y = y;
        if (z !== undefined) op.Insert.z = z;
    }
};

const resolveDoorTuning = (
    inputs: NormalizeLShapeInputs
): LShapeDoorTuning => ({
    ...defaultLShapeDoorTuning,
    ...inputs.doorTuning,
});

export type LShapeDoorPlacement = {
    insert: {x: number; y: number; z: number};
    perimeterWidth: number;
    appliedShift?: {insertDepth: number; alongLeg: number};
};

type BifoldDoorFlags = Pick<
    OperationGroup,
    'IsBifoldLeftDoor' | 'IsBifoldRightDoor' | 'IsLeftBifoldExtension'
>;

export const getLShapeBifoldDoorPlacement = (
    og: BifoldDoorFlags,
    inputs: NormalizeLShapeInputs
): LShapeDoorPlacement | null => {
    const isLeftLegDoor = og.IsBifoldLeftDoor && !og.IsLeftBifoldExtension;
    const isRightLegDoor = og.IsBifoldRightDoor && !og.IsLeftBifoldExtension;
    if (!isLeftLegDoor && !isRightLegDoor) return null;

    const doorThickness = resolveDoorThickness(inputs);
    const tuning = resolveDoorTuning(inputs);

    const legFaceWidth = isLeftLegDoor
        ? inputs.cabinetLeftWidth - inputs.cabinetRightDepth
        : inputs.cabinetRightWidth - inputs.cabinetLeftDepth;

    // Each leg door insets by its own side's margin only; the corner seam is
    // doorThickness + cornerGap.
    const ownSideMargin = isLeftLegDoor ? inputs.doorLeft : inputs.doorRight;

    const perimeterWidth =
        legFaceWidth - ownSideMargin - doorThickness - tuning.cornerGap;

    if (isLeftLegDoor) {
        return {
            perimeterWidth,
            insert: {
                x: inputs.cabinetLeftDepth + tuning.depthGap,
                y: inputs.doorLeft + inputs.carc,
                z: -1,
            },
        };
    }

    const leftLegFace = inputs.cabinetLeftWidth - inputs.cabinetRightDepth;
    const insertDepth = doorThickness + tuning.depthGap;
    return {
        perimeterWidth,
        insert: {
            x: inputs.cabinetLeftDepth + insertDepth,
            y: leftLegFace + (inputs.carc - tuning.cornerGap),
            z: -1,
        },
        appliedShift: {
            insertDepth,
            alongLeg: doorThickness * tuning.openCompensationFactor,
        },
    };
};

export const getRightLShapeBifoldDoorX = (
    cabinetRightWidth: number,
    doorRight: number,
    isLShapedPantry: boolean,
    exteriorThickness: number,
    carcaseThickness: number
): number =>
    cabinetRightWidth -
    doorRight +
    (isLShapedPantry ? exteriorThickness || carcaseThickness : 0);

// Shared by handler.ts and useDoorAndDrawerAnimation.ts to keep them in sync.
export const getPantryAwareRightLShapeBifoldDoorX = (
    mesh: THREE.Object3D,
    meshPosX: number,
    isLShapedPantry: boolean,
    cabinetRightWidth: number,
    doorRight: number,
    exteriorThickness: number,
    carcaseThickness: number
): number => {
    if (isLShapedPantry && !mesh.userData?.isShakerDoor) {
        const size = new THREE.Vector3();
        new THREE.Box3().setFromObject(mesh).getSize(size);
        return meshPosX + size.x;
    }
    return getRightLShapeBifoldDoorX(
        cabinetRightWidth,
        doorRight,
        isLShapedPantry,
        exteriorThickness,
        carcaseThickness
    );
};

// Flat doors only; shaker doors get their offset from CreateShakerDoor.
export const getRightLShapeBifoldLeftDoorXOffset = (
    isRightLShape: boolean,
    isLeftBifoldExtension: boolean,
    exteriorThickness: number
): number => (isRightLShape && !isLeftBifoldExtension ? exteriorThickness : 0);

const rewriteBifoldDoorGeometry = (
    og: OperationGroup,
    inputs: NormalizeLShapeInputs
) => {
    const placement = getLShapeBifoldDoorPlacement(og, inputs);
    if (!placement) return;

    const tuning = resolveDoorTuning(inputs);
    const doorHeight =
        inputs.cabinetHeight - inputs.doorTop - inputs.doorBottom;
    const {perimeterAnchor} = tuning;

    setPerimeterRect(
        og,
        perimeterAnchor,
        perimeterAnchor + doorHeight,
        perimeterAnchor,
        perimeterAnchor + placement.perimeterWidth
    );
    // Bottom margin lives on Insert.z (shaker doors ignore perimeter offsets).
    setPanelInsert(
        og,
        placement.insert.x,
        placement.insert.y,
        placement.insert.z + inputs.doorBottom
    );
    if (placement.appliedShift) og.AppliedDoorShift = placement.appliedShift;
};

type ThreeDoorRole = 'big' | 'smallInner' | 'smallOuter';

type DoorOGSummary = {
    og: OperationGroup;
    insertY: number;
    sizeY: number;
};

const summarizeDoorOG = (og: OperationGroup): DoorOGSummary | null => {
    let insertY: number | undefined;
    let yMin = Infinity;
    let yMax = -Infinity;
    let hasPerim = false;
    for (const op of og.Operations ?? []) {
        if (op.Type === 'panel' && op.Insert) {
            insertY = op.Insert.y;
        }
        if (op.Type === 'perimeter_shape' && op.Segments) {
            hasPerim = true;
            for (const seg of op.Segments) {
                for (const c of [seg.StartCoor, seg.EndCoor]) {
                    if (c?.y !== undefined) {
                        if (c.y < yMin) yMin = c.y;
                        if (c.y > yMax) yMax = c.y;
                    }
                }
            }
        }
    }
    if (insertY === undefined || !hasPerim) return null;
    return {og, insertY, sizeY: yMax - yMin};
};

const classify3DoorOGs = (
    ogs: Record<string, OperationGroup>
): Map<OperationGroup, ThreeDoorRole> => {
    const result = new Map<OperationGroup, ThreeDoorRole>();
    const candidates: OperationGroup[] = [];
    for (const og of Object.values(ogs)) {
        if (og.PartID !== 'IDP-1') continue;
        // Match by flag: Insert.z carries the bottom margin.
        const isDoor =
            og.IsBifoldLeftDoor ||
            og.IsBifoldRightDoor ||
            og.IsRightDoor ||
            og.IsPairDoor;
        if (!isDoor) continue;
        const summary = summarizeDoorOG(og);
        if (!summary) continue;
        candidates.push(og);
    }
    if (candidates.length < 3) return result;

    const isRight = candidates.some((og) => og.IsRightLShape);

    for (const og of candidates) {
        if (isRight) {
            if (og.IsBifoldRightDoor) {
                result.set(og, 'big');
            } else if (og.IsBifoldLeftDoor && og.IsLeftBifoldExtension) {
                result.set(og, 'smallOuter');
            } else if (og.IsBifoldLeftDoor) {
                result.set(og, 'smallInner');
            }
        } else {
            if (og.IsBifoldLeftDoor) {
                result.set(og, 'big');
            } else if (og.IsBifoldRightDoor) {
                result.set(og, 'smallInner');
            } else {
                result.set(og, 'smallOuter');
            }
        }
    }
    return result;
};

export const getLShape3DoorPlacement = (
    role: ThreeDoorRole,
    isRight: boolean,
    inputs: NormalizeLShapeInputs
): LShapeDoorPlacement => {
    const doorThickness = resolveDoorThickness(inputs);
    const tuning = resolveDoorTuning(inputs);

    const bigLegFace = isRight
        ? inputs.cabinetRightWidth - inputs.cabinetLeftDepth
        : inputs.cabinetLeftWidth - inputs.cabinetRightDepth;
    const smallLegFace = isRight
        ? inputs.cabinetLeftWidth - inputs.cabinetRightDepth
        : inputs.cabinetRightWidth - inputs.cabinetLeftDepth;
    const smallLegDepth = isRight
        ? inputs.cabinetLeftDepth
        : inputs.cabinetRightDepth;

    // Each leg insets by its own outer margin. Right L: big leg is the right
    // leg, small leg the left; mirrored for a left L.
    const bigOuterMargin = isRight ? inputs.doorRight : inputs.doorLeft;
    const smallOuterMargin = isRight ? inputs.doorLeft : inputs.doorRight;

    const smallW =
        (smallLegFace - smallOuterMargin - inputs.doorGap) / 2 -
        doorThickness / 2;
    // The seam between the small doors is the door gap reserved in smallW,
    // so the pair keeps its total span and a wider gap shrinks the doors.
    const innerDoorGap = inputs.doorGap;

    if (role === 'big') {
        const perimeterWidth =
            bigLegFace - bigOuterMargin - doorThickness - tuning.cornerGap;
        const insert = isRight
            ? {
                  x: smallLegDepth + doorThickness + tuning.depthGap,
                  y: smallLegFace + (inputs.carc - tuning.cornerGap),
                  z: -1,
              }
            : {
                  x: inputs.cabinetLeftDepth + tuning.depthGap,
                  y: inputs.doorLeft + inputs.carc,
                  z: -1,
              };
        return {perimeterWidth, insert};
    }

    if (isRight) {
        const smallInsertX = smallLegDepth + tuning.depthGap;
        const smallYStart = inputs.doorLeft + inputs.carc;
        return {
            perimeterWidth: smallW,
            insert: {
                x: smallInsertX,
                y:
                    role === 'smallInner'
                        ? smallYStart + smallW + innerDoorGap
                        : smallYStart,
                z: -1,
            },
        };
    }

    // Depth/corner anchor for the small (right-leg) pair: it sits just past the
    // end of the big leg face. This axis is perpendicular to both reveals, so
    // neither margin belongs here — the old `+ doorLeft` term pushed the small
    // doors into the cabinet whenever the left reveal changed.
    const smallInsertY = bigLegFace + tuning.leftSmallInsertYAdjust;
    const innerInsertX =
        inputs.cabinetLeftDepth + doorThickness + tuning.depthGap;
    const outerInsertX = innerInsertX + smallW + innerDoorGap;
    const alongLegAdjust = inputs.carc - tuning.cornerGap;

    if (role === 'smallInner') {
        return {
            perimeterWidth: smallW,
            insert: {x: innerInsertX, y: smallInsertY + alongLegAdjust, z: -1},
            appliedShift: {
                insertDepth: doorThickness,
                alongLeg: doorThickness * tuning.openCompensationFactor,
            },
        };
    }
    return {
        perimeterWidth: smallW,
        insert: {x: outerInsertX, y: smallInsertY + alongLegAdjust, z: -1},
    };
};

const rewrite3DoorGeometry = (
    og: OperationGroup,
    role: ThreeDoorRole,
    inputs: NormalizeLShapeInputs
) => {
    const tuning = resolveDoorTuning(inputs);
    const doorHeight =
        inputs.cabinetHeight - inputs.doorTop - inputs.doorBottom;
    const {perimeterAnchor} = tuning;

    const placement = getLShape3DoorPlacement(
        role,
        Boolean(og.IsRightLShape),
        inputs
    );

    setPerimeterRect(
        og,
        perimeterAnchor,
        perimeterAnchor + doorHeight,
        perimeterAnchor,
        perimeterAnchor + placement.perimeterWidth
    );
    // Bottom margin lives on Insert.z (shaker doors ignore perimeter offsets).
    setPanelInsert(
        og,
        placement.insert.x,
        placement.insert.y,
        placement.insert.z + inputs.doorBottom
    );
    if (placement.appliedShift) og.AppliedDoorShift = placement.appliedShift;
};

export const rewriteLShapeDoors = (
    ogs: Record<string, OperationGroup>,
    variant: LShapeVariant,
    inputs: NormalizeLShapeInputs
) => {
    const threeDoorRoles =
        variant === '3door' || variant === 'upper3door'
            ? classify3DoorOGs(ogs)
            : null;

    for (const og of Object.values(ogs)) {
        if (!og.PartID) continue;

        if (threeDoorRoles) {
            const role = threeDoorRoles.get(og);
            if (role) {
                rewrite3DoorGeometry(og, role, inputs);
                continue;
            }
        }

        const isBifoldDoorOG =
            (og.IsBifoldLeftDoor || og.IsBifoldRightDoor) &&
            !og.IsLeftBifoldExtension &&
            (variant === 'base' || variant === 'upper');
        if (isBifoldDoorOG) {
            rewriteBifoldDoorGeometry(og, inputs);
        }
    }
};

type PanelAdjustment = {
    farEdgeX?: number;
    farEdgeY?: number;
    nearEdgeX?: number;
    nearEdgeY?: number;
    moveX?: number;
    moveY?: number;
    moveZ?: number;
};

const PART_ADJUSTMENT_BUCKETS: Array<{suffix: string; key: string}> = [
    {suffix: 'BaseLeftDepth', key: 'baseLeftDepth'},
    {suffix: 'BaseRightDepth', key: 'baseRightDepth'},
    {suffix: 'BaseLeftWidth', key: 'baseLeftWidth'},
    {suffix: 'BaseRightWidth', key: 'baseRightWidth'},
    {suffix: 'DoorLeftBifold', key: 'doorLeftBifold'},
    {suffix: 'DoorRightBifold', key: 'doorRightBifold'},
    {suffix: 'LsRailOnEdge2', key: 'lsRailOnEdge2'},
    {suffix: 'LsRailOnEdge', key: 'lsRailOnEdge'},
];

// Carcass-panel adjustments shared by every L-shape variant. The four entries
// here are identical across base/upper/3door; baseLeftWidth and lsRailOnEdge2
// are the only two that vary, so resolveAdjustments overrides those per variant.
const commonCarcassAdjustments = (carc: number): Map<string, PanelAdjustment> =>
    new Map<string, PanelAdjustment>([
        ['baseLeftDepth', {farEdgeY: 1, moveY: carc * 2 + 2}],
        ['baseRightDepth', {farEdgeY: -carc, moveX: -carc}],
        ['baseRightWidth', {farEdgeY: -carc, moveX: -carc}],
        ['lsRailOnEdge', {moveY: carc, farEdgeX: -carc}],
        ['baseLeftWidth', {farEdgeY: -(carc * 2) - 2, moveY: 0}],
        ['lsRailOnEdge2', {farEdgeX: -(carc + 2), moveY: carc}],
    ]);

const isUpperVariant = (variant: LShapeVariant) =>
    variant === 'upper' || variant === 'upper3door';

const applyAdjustmentToPart = (
    og: OperationGroup,
    adjustment: PanelAdjustment
) => {
    for (const op of og.Operations ?? []) {
        if (op.Type === 'perimeter_shape' && op.Segments) {
            let xMin = Infinity;
            let xMax = -Infinity;
            let yMin = Infinity;
            let yMax = -Infinity;
            for (const seg of op.Segments) {
                for (const c of [seg.StartCoor, seg.EndCoor]) {
                    if (c?.x !== undefined) {
                        if (c.x < xMin) xMin = c.x;
                        if (c.x > xMax) xMax = c.x;
                    }
                    if (c?.y !== undefined) {
                        if (c.y < yMin) yMin = c.y;
                        if (c.y > yMax) yMax = c.y;
                    }
                }
            }
            for (const seg of op.Segments) {
                for (const c of [seg.StartCoor, seg.EndCoor]) {
                    if (!c) continue;
                    if (
                        adjustment.farEdgeX !== undefined &&
                        c.x !== undefined &&
                        Math.abs(c.x - xMax) < 0.01
                    ) {
                        c.x = xMax + adjustment.farEdgeX;
                    }
                    if (
                        adjustment.farEdgeY !== undefined &&
                        c.y !== undefined &&
                        Math.abs(c.y - yMax) < 0.01
                    ) {
                        c.y = yMax + adjustment.farEdgeY;
                    }
                    if (
                        adjustment.nearEdgeX !== undefined &&
                        c.x !== undefined &&
                        Math.abs(c.x - xMin) < 0.01
                    ) {
                        c.x = xMin + adjustment.nearEdgeX;
                    }
                    if (
                        adjustment.nearEdgeY !== undefined &&
                        c.y !== undefined &&
                        Math.abs(c.y - yMin) < 0.01
                    ) {
                        c.y = yMin + adjustment.nearEdgeY;
                    }
                }
            }
        }
        if (op.Type === 'panel' && op.Insert) {
            if (adjustment.moveX !== undefined && op.Insert.x !== undefined)
                op.Insert.x = op.Insert.x + adjustment.moveX;
            if (adjustment.moveY !== undefined && op.Insert.y !== undefined)
                op.Insert.y = op.Insert.y + adjustment.moveY;
            if (adjustment.moveZ !== undefined && op.Insert.z !== undefined)
                op.Insert.z = op.Insert.z + adjustment.moveZ;
        }
    }
};

export const classify3DoorBucket = (
    og: OperationGroup,
    inputs: Pick<
        NormalizeLShapeInputs,
        | 'cabinetLeftDepth'
        | 'cabinetRightDepth'
        | 'cabinetLeftWidth'
        | 'cabinetRightWidth'
        | 'carc'
    >
): string | undefined => {
    let insert: {x?: number; y?: number; z?: number} | undefined;
    let perimMaxX = -Infinity;
    let perimMaxY = -Infinity;
    let hasPerim = false;
    for (const op of og.Operations ?? []) {
        if (op.Type === 'panel' && !insert) insert = op.Insert;
        if (op.Type === 'perimeter_shape' && op.Segments) {
            hasPerim = true;
            for (const seg of op.Segments) {
                for (const c of [seg.StartCoor, seg.EndCoor]) {
                    if (c?.x !== undefined && c.x > perimMaxX) perimMaxX = c.x;
                    if (c?.y !== undefined && c.y > perimMaxY) perimMaxY = c.y;
                }
            }
        }
    }
    if (!insert) return undefined;

    const eq = (a: number | undefined, b: number, tol = 0.5) =>
        a !== undefined && Math.abs(a - b) < tol;

    // Parts are classified by where they sit in the cabinet's coordinate space.
    const RAIL_ON_EDGE_MIN_Z = 100; // rails are inserted on-edge, deep in z
    const RAIL_STRIP_SIZE = 69; // a rail's narrow edge measures ~69mm
    const RAIL_STRIP_TOL = 2;
    const LONG_LEG_PERIM_FRACTION = 0.75; // back rail spans most of the long leg
    const DOOR_FACE_Z = -1; // door faces sit flush at z = -1
    const BIG_DOOR_MIN_PERIM_Y = 300; // big-leg door is taller than a small-leg

    const isRailOnEdge =
        insert.z !== undefined &&
        insert.z > RAIL_ON_EDGE_MIN_Z &&
        hasPerim &&
        (eq(perimMaxY, RAIL_STRIP_SIZE, RAIL_STRIP_TOL) ||
            eq(perimMaxX, RAIL_STRIP_SIZE, RAIL_STRIP_TOL));
    if (isRailOnEdge) {
        const maxWidth = Math.max(
            inputs.cabinetLeftWidth,
            inputs.cabinetRightWidth
        );
        return perimMaxX > maxWidth * LONG_LEG_PERIM_FRACTION
            ? 'lsRailOnEdge'
            : 'lsRailOnEdge2';
    }

    if (eq(insert.z, DOOR_FACE_Z)) {
        if (perimMaxY > BIG_DOOR_MIN_PERIM_Y) return 'door3DoorBig';
        if (
            insert.x !== undefined &&
            insert.x < (inputs.cabinetLeftDepth + inputs.cabinetRightWidth) / 2
        ) {
            return 'door3DoorSmallLeft';
        }
        return 'door3DoorSmallRight';
    }
    if (eq(insert.x, -1)) return 'baseLeftDepth';
    if (
        eq(insert.x, inputs.cabinetRightWidth) &&
        eq(insert.y, inputs.cabinetLeftWidth + inputs.carc)
    ) {
        return 'baseRightDepth';
    }
    if (
        eq(insert.x, inputs.cabinetRightWidth) &&
        eq(insert.y, inputs.cabinetLeftWidth)
    ) {
        return 'baseRightWidth';
    }
    if (eq(insert.x, inputs.carc) && eq(insert.y, inputs.cabinetLeftWidth)) {
        return 'baseLeftWidth';
    }
    return undefined;
};

export const resolveAdjustments = (
    variant: LShapeVariant,
    carc: number,
    isRightLShape = false
): Map<string, PanelAdjustment> => {
    const adjustments = commonCarcassAdjustments(carc);
    const is3Door = variant === '3door' || variant === 'upper3door';

    // Upper variants seat the left-width panel one carc in, not two.
    if (isUpperVariant(variant)) {
        adjustments.set('baseLeftWidth', {farEdgeY: -carc, moveY: carc});
    }
    // Upper 3-door trims the back rail by one carc instead of carc+2.
    if (variant === 'upper3door') {
        adjustments.set('lsRailOnEdge2', {farEdgeX: -carc, moveY: carc});
    }
    // Right-handed 3-doors mirror the rails onto the opposite leg.
    if (isRightLShape && is3Door) {
        adjustments.set('lsRailOnEdge', {farEdgeX: -(carc * 2) - 2});
        adjustments.set('lsRailOnEdge2', {farEdgeX: -carc, moveY: carc});
    }
    return adjustments;
};

export const normalizeLShapeCabinet = (
    data: Batch,
    templateName: string | undefined,
    variables: TemplateVariable,
    inputs: NormalizeLShapeInputs
) => {
    if (inputs.isBaseCorner) return;

    // Pantry L-shapes are plain templates: their carcass parts are generic
    // (Base Left/Right Side, Back Vertical) rather than the named L-correction
    // panels the base templates carry, so the correction pass can only rewrite
    // their doors (matched by IsBifold* flags) while leaving the carcass raw,
    // which pulls the doors out of alignment. The pantry templates are already
    // authored correctly, so skip the correction pass entirely for them.
    if (variables?.isLShapedPantry) return;

    const variant = detectLShapeVariant(templateName, variables);
    if (!variant) return;

    const requiredDims = [
        inputs.cabinetLeftDepth,
        inputs.cabinetRightDepth,
        inputs.cabinetLeftWidth,
        inputs.cabinetRightWidth,
        inputs.cabinetHeight,
        inputs.carc,
    ];
    if (requiredDims.some((d) => !Number.isFinite(d))) return;

    const ogs =
        data?.Jobs?.['IDJ-1']?.Manufacturing?.['IDMFG-1']?.OperationGroups;
    if (!ogs) return;

    const isRightLShapeCabinet = Object.values(ogs).some(
        (og) => og.IsRightLShape
    );

    const adjustments = resolveAdjustments(
        variant,
        inputs.carc,
        isRightLShapeCabinet
    );
    if (adjustments.size === 0) return;

    rewriteLShapeDoors(ogs, variant, inputs);

    for (const og of Object.values(ogs)) {
        if (!og.PartID) continue;

        let bucketKey: string | undefined;
        if (variant === '3door' || variant === 'upper3door') {
            bucketKey = classify3DoorBucket(og, inputs);
        } else {
            for (const bucket of PART_ADJUSTMENT_BUCKETS) {
                if (og.PartID.endsWith(bucket.suffix)) {
                    bucketKey = bucket.key;
                    break;
                }
            }
        }
        if (!bucketKey) continue;
        if (
            bucketKey === 'doorLeftBifold' ||
            bucketKey === 'doorRightBifold' ||
            bucketKey === 'door3DoorBig' ||
            bucketKey === 'door3DoorSmallLeft' ||
            bucketKey === 'door3DoorSmallRight'
        ) {
            continue;
        }
        const adjustment = adjustments.get(bucketKey);
        if (!adjustment) continue;
        applyAdjustmentToPart(og, adjustment);
    }
};

const shiftPerimeterY = (og: OperationGroup, deltaY: number) => {
    if (!deltaY) return;
    for (const op of og.Operations ?? []) {
        if (op.Type !== 'perimeter_shape' || !op.Segments) continue;
        for (const seg of op.Segments) {
            if (seg.StartCoor && seg.StartCoor.y != null) {
                seg.StartCoor.y = Number(seg.StartCoor.y) + deltaY;
            }
            if (seg.EndCoor && seg.EndCoor.y != null) {
                seg.EndCoor.y = Number(seg.EndCoor.y) + deltaY;
            }
        }
    }
};

const remapPairHalfPerimeter = (
    og: OperationGroup,
    ownReveal: number,
    seamPush: number
) => {
    for (const op of og.Operations ?? []) {
        if (op.Type !== 'perimeter_shape' || !op.Segments) continue;
        let yMin = Infinity;
        let yMax = -Infinity;
        for (const seg of op.Segments) {
            for (const c of [seg.StartCoor, seg.EndCoor]) {
                if (c?.y == null) continue;
                const y = Number(c.y);
                if (y < yMin) yMin = y;
                if (y > yMax) yMax = y;
            }
        }
        if (!Number.isFinite(yMin) || !Number.isFinite(yMax) || yMin === yMax) {
            continue;
        }
        const outerNew = yMin + ownReveal;
        const seamNew = yMax + seamPush;
        const tol = 0.5;
        for (const seg of op.Segments) {
            for (const c of [seg.StartCoor, seg.EndCoor]) {
                if (c?.y == null) continue;
                const y = Number(c.y);
                if (Math.abs(y - yMax) <= tol) c.y = seamNew;
                else if (Math.abs(y - yMin) <= tol) c.y = outerNew;
            }
        }
    }
};

export const correctBaseCornerDoorMargins = (
    data: Batch,
    inputs: NormalizeLShapeInputs
) => {
    // Corner pantries share the same corner "hang" doors as base corners; they
    // only differ by carrying upper_shelves, which flips them off isBaseCorner.
    // Run the reveal correction for both. For shaker doors the rewritten flat
    // perimeter is skipped at render time, so this is a no-op there.
    if (!inputs.isBaseCorner && !inputs.isCornerPantry) return;
    const ogs =
        data?.Jobs?.['IDJ-1']?.Manufacturing?.['IDMFG-1']?.OperationGroups;
    if (!ogs) return;
    const doorOgs = Object.values(ogs).filter(
        (og) => og.IsCornerLeftDoor || og.IsCornerRightDoor
    );
    if (doorOgs.length === 0) return;

    const doorLeft = inputs.doorLeft ?? 0;
    const doorRight = inputs.doorRight ?? 0;
    const isPair = doorOgs.length > 1;

    if (isPair) {
        const seamPush = (doorLeft + doorRight) / 2;
        for (const og of doorOgs) {
            const ownReveal = og.IsCornerLeftDoor ? doorLeft : doorRight;
            remapPairHalfPerimeter(og, ownReveal, seamPush);
        }
        return;
    }

    for (const og of doorOgs) {
        const reveal = og.IsCornerLeftDoor ? doorLeft : doorRight;
        shiftPerimeterY(og, reveal);
    }
};
